import { lazy, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { AppShell } from '@/layouts/AppShell';
import { ErrorState, Spinner } from '@/components/ui';
import { HRMS_NAV, TASK_NAV } from './navigation';

const p = <T,>(f: () => Promise<{ default: T }>) => lazy(f as never);

// Common
const LoginPage = p(() => import('@/modules/common/pages/LoginPage'));
const ForgotPasswordPage = p(() => import('@/modules/common/pages/ForgotPasswordPage'));
const ResetPasswordPage = p(() => import('@/modules/common/pages/ResetPasswordPage'));
const PrivacyPolicyPage = p(() => import('@/modules/common/pages/PrivacyPolicyPage'));
const TermsPage = p(() => import('@/modules/common/pages/TermsPage'));
const NotificationsPage = p(() => import('@/modules/common/pages/NotificationsPage'));
const ProfilePage = p(() => import('@/modules/common/pages/ProfilePage'));
const SettingsPage = p(() => import('@/modules/common/pages/SettingsPage'));
const AuditLogsPage = p(() => import('@/modules/common/pages/AuditLogsPage'));
const ExportsPage = p(() => import('@/modules/common/pages/ExportsPage'));
const NotFoundPage = p(() => import('@/modules/common/pages/NotFoundPage'));

// HRMS
const HrmsDashboard = p(() => import('@/modules/hrms/pages/HrmsDashboard'));
const EmployeesPage = p(() => import('@/modules/hrms/pages/EmployeesPage'));
const EmployeeFormPage = p(() => import('@/modules/hrms/pages/EmployeeFormPage'));
const EmployeeProfilePage = p(() => import('@/modules/hrms/pages/EmployeeProfilePage'));
const DepartmentsPage = p(() => import('@/modules/hrms/pages/DepartmentsPage'));
const DesignationsPage = p(() => import('@/modules/hrms/pages/DesignationsPage'));
const TeamsPage = p(() => import('@/modules/hrms/pages/TeamsPage'));
const AttendancePage = p(() => import('@/modules/hrms/pages/AttendancePage'));
const AttendanceDayPage = p(() => import('@/modules/hrms/pages/AttendanceDayPage'));
const LeavePage = p(() => import('@/modules/hrms/pages/LeavePage'));
const LeaveRequestPage = p(() => import('@/modules/hrms/pages/LeaveRequestPage'));
const RegularisationsPage = p(() => import('@/modules/hrms/pages/RegularisationsPage'));
const RegularisationFormPage = p(() => import('@/modules/hrms/pages/RegularisationFormPage'));
const RegularisationDetailPage = p(() => import('@/modules/hrms/pages/RegularisationDetailPage'));
const LeavePoliciesPage = p(() => import('@/modules/hrms/pages/LeavePoliciesPage'));
const HolidaysPage = p(() => import('@/modules/hrms/pages/HolidaysPage'));
const HrmsReportsPage = p(() => import('@/modules/hrms/pages/HrmsReportsPage'));
const RolesPage = p(() => import('@/modules/hrms/pages/RolesPage'));

// Tasks
const TaskDashboard = p(() => import('@/modules/tasks/pages/TaskDashboard'));
const MyTasksPage = p(() => import('@/modules/tasks/pages/MyTasksPage'));
const TaskListPage = p(() => import('@/modules/tasks/pages/TaskListPage'));
const TaskFormPage = p(() => import('@/modules/tasks/pages/TaskFormPage'));
const TaskDetailPage = p(() => import('@/modules/tasks/pages/TaskDetailPage'));
const SchedulesPage = p(() => import('@/modules/tasks/pages/SchedulesPage'));
const ScheduleDetailPage = p(() => import('@/modules/tasks/pages/ScheduleDetailPage'));
const ProjectsPage = p(() => import('@/modules/tasks/pages/ProjectsPage'));
const ProjectDetailPage = p(() => import('@/modules/tasks/pages/ProjectDetailPage'));
const OutletsPage = p(() => import('@/modules/tasks/pages/OutletsPage'));
const OutletTasksPage = p(() => import('@/modules/tasks/pages/OutletTasksPage'));
const IssuesPage = p(() => import('@/modules/tasks/pages/IssuesPage'));
const IssueDetailPage = p(() => import('@/modules/tasks/pages/IssueDetailPage'));
const CalendarPage = p(() => import('@/modules/tasks/pages/CalendarPage'));
const TaskReportsPage = p(() => import('@/modules/tasks/pages/TaskReportsPage'));
const TaskSetupPage = p(() => import('@/modules/tasks/pages/TaskSetupPage'));

function FullScreenLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner />
    </div>
  );
}

function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <AppShell />;
}

function PublicOnly() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Route-level permission guard (the API enforces the same rules). */
function Guard({ perms, children }: { perms: string[]; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(...perms)) return <ErrorState error={Object.assign(new Error('You do not have access to this page.'), { status: 403 }) as never} />;
  return <>{children}</>;
}

function Home() {
  const { can } = useAuth();
  const first = [...HRMS_NAV, ...TASK_NAV].find((i) => can(...i.perms));
  return <Navigate to={can('hrms_dashboard:view') ? '/hrms/dashboard' : (first?.to ?? '/profile')} replace />;
}

const g = (perms: string[], el: ReactNode) => <Guard perms={perms}>{el}</Guard>;

export const router = createBrowserRouter([
  {
    element: <PublicOnly />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
  { path: '/terms', element: <TermsPage /> },
  {
    element: <RequireAuth />,
    children: [
      { index: true, path: '/', element: <Home /> },
      { path: '/notifications', element: <NotificationsPage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/settings', element: g(['settings:view'], <SettingsPage />) },
      { path: '/audit-logs', element: g(['audit:view'], <AuditLogsPage />) },
      { path: '/reports/exports', element: <ExportsPage /> },

      { path: '/hrms', element: <Navigate to="/hrms/dashboard" replace /> },
      { path: '/hrms/dashboard', element: g(['hrms_dashboard:view'], <HrmsDashboard />) },
      { path: '/hrms/employees', element: g(['employees:view'], <EmployeesPage />) },
      { path: '/hrms/employees/new', element: g(['employees:create'], <EmployeeFormPage />) },
      { path: '/hrms/employees/:id', element: <EmployeeProfilePage /> },
      { path: '/hrms/employees/:id/edit', element: g(['employees:edit'], <EmployeeFormPage />) },
      { path: '/hrms/departments', element: g(['departments:view'], <DepartmentsPage />) },
      { path: '/hrms/designations', element: g(['designations:view'], <DesignationsPage />) },
      { path: '/hrms/teams', element: g(['teams:view', 'manager_mappings:view'], <TeamsPage />) },
      { path: '/hrms/attendance', element: g(['attendance:view'], <AttendancePage />) },
      { path: '/hrms/attendance/:employeeId/:date', element: g(['attendance:view'], <AttendanceDayPage />) },
      { path: '/hrms/leave', element: g(['leave:view'], <LeavePage />) },
      { path: '/hrms/leave/requests/:id', element: g(['leave:view'], <LeaveRequestPage />) },
      { path: '/hrms/regularisations', element: g(['regularisations:view'], <RegularisationsPage />) },
      { path: '/hrms/regularisations/new', element: g(['regularisations:create'], <RegularisationFormPage />) },
      { path: '/hrms/regularisations/:id', element: g(['regularisations:view'], <RegularisationDetailPage />) },
      { path: '/hrms/leave-policies', element: g(['leave_policies:view'], <LeavePoliciesPage />) },
      { path: '/hrms/holidays', element: g(['holidays:view'], <HolidaysPage />) },
      { path: '/hrms/reports', element: g(['hrms_reports:view'], <HrmsReportsPage />) },
      { path: '/hrms/roles', element: g(['roles:view', 'roles:manage'], <RolesPage />) },

      { path: '/tasks', element: <Navigate to="/tasks/dashboard" replace /> },
      { path: '/tasks/dashboard', element: g(['task_dashboard:view'], <TaskDashboard />) },
      { path: '/tasks/my', element: g(['tasks:view'], <MyTasksPage />) },
      { path: '/tasks/list', element: g(['tasks:view'], <TaskListPage />) },
      { path: '/tasks/new', element: g(['tasks:create'], <TaskFormPage />) },
      { path: '/tasks/items/:id', element: g(['tasks:view'], <TaskDetailPage />) },
      { path: '/tasks/items/:id/edit', element: g(['tasks:view'], <TaskFormPage />) },
      { path: '/tasks/schedules', element: g(['schedules:view'], <SchedulesPage />) },
      { path: '/tasks/schedules/:id', element: g(['schedules:view'], <ScheduleDetailPage />) },
      { path: '/tasks/projects', element: g(['projects:view'], <ProjectsPage />) },
      { path: '/tasks/projects/:id', element: g(['projects:view'], <ProjectDetailPage />) },
      { path: '/tasks/outlets', element: g(['outlets:view'], <OutletsPage />) },
      { path: '/tasks/outlet-tasks', element: g(['tasks:view'], <OutletTasksPage />) },
      { path: '/tasks/issues', element: g(['issues:view'], <IssuesPage />) },
      { path: '/tasks/issues/:id', element: g(['issues:view'], <IssueDetailPage />) },
      { path: '/tasks/calendar', element: g(['calendar:view'], <CalendarPage />) },
      { path: '/tasks/reports', element: g(['task_reports:view'], <TaskReportsPage />) },
      { path: '/tasks/setup', element: g(['task_categories:view'], <TaskSetupPage />) },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
